/**
 * Soft-safe share view-model seam (arch Move 1).
 * Pure: HandState → layer presence + human chronology inputs.
 * Consumed by Share UI card, Copy (`shareCopy.buildHumanSummary`), PNG (`imageExport`).
 *
 * Identity lock: process share only (see docs/architecture.md Soft-refuse).
 * No fabricated empty layers; process tags stay off this surface
 * (`shareHuman` chronology only). Soft 14 ops-only: no crawl locale fill.
 * SoftGate / edit / success chrome never enter the payload.
 */
import type { Card, HandState, Street, StreetAction } from './types'
import {
  buildHumanActionChronologyLines,
  buildPngActionChronologyLines,
  groupActionsByStreet,
} from './shareHuman'
import { buildProcessTimeline, type ProcessTimeline } from './processTimeline'
import {
  shareHasBoard,
  shareHasHeroHoles,
  shareHasLine,
  shareHasTakeaway,
  shareHasVillains,
} from './shareLayers'

/** Soft #2 screen/story order (filled layers only). */
export type ShareStoryLayer = 'line' | 'holes' | 'board' | 'takeaway'

export type ShareLayerPresence = {
  line: boolean
  holes: boolean
  board: boolean
  villains: boolean
  takeaway: boolean
}

export type ShareViewModel = {
  layers: ShareLayerPresence
  /** Soft #2: line → holes → board → takeaway (omit empties). */
  storyOrder: ShareStoryLayer[]
  /** Tag-free street groups for Share UI. */
  actionsByStreet: { street: Street; items: StreetAction[] }[]
  /**
   * Denser street process timeline (light→heavy). Empty-street free.
   * Soft-refuse: process density only — never solver metrics.
   */
  timeline: ProcessTimeline
  /** Present board cards only; never a fabricated placeholder. */
  boardCards: Card[]
  /**
   * Trimmed takeaway when the layer is shown; else null.
   * Incomplete hands suppress takeaway so Share / Copy / PNG tell one story.
   */
  takeaway: string | null
  /**
   * SoftGate-aligned Incomplete stamp.
   * Soft C1 Soft-safe Quiet: L1.0 holes-only (holes present, zero actions) is
   * not incomplete — SoftGate pressure / Incomplete badge stay off.
   */
  incomplete: boolean
}

function incompleteFromHand(state: HandState): boolean {
  // Soft C1 Soft-safe Quiet — align with handLooksIncomplete (holes gap only).
  return !state.holeCards[0] || !state.holeCards[1]
}

/**
 * Build the share view-model. Pure: no DOM, i18n, or SoftGate/edit chrome.
 */
export function buildShareViewModel(state: HandState): ShareViewModel {
  const incomplete = incompleteFromHand(state)
  const line = shareHasLine(state.actions)
  const villains = shareHasVillains(state)
  const holes = shareHasHeroHoles(state.holeCards) || villains
  const board = shareHasBoard(state.board)
  // Same story across Share / Copy / PNG: suppress takeaway while incomplete.
  const takeawayText =
    !incomplete && shareHasTakeaway(state.takeaway)
      ? state.takeaway!.trim()
      : null
  const takeaway = Boolean(takeawayText)

  const boardCards = [
    ...state.board.flop,
    state.board.turn,
    state.board.river,
  ].filter((c): c is Card => Boolean(c))

  const storyOrder: ShareStoryLayer[] = []
  if (line) storyOrder.push('line')
  if (holes) storyOrder.push('holes')
  if (board) storyOrder.push('board')
  if (takeaway) storyOrder.push('takeaway')

  return {
    layers: { line, holes, board, villains, takeaway },
    storyOrder,
    actionsByStreet: line ? groupActionsByStreet(state.actions) : [],
    timeline: buildProcessTimeline(state),
    boardCards,
    takeaway: takeawayText,
    incomplete,
  }
}

/**
 * Copy human chronology lines (only when the line layer is present).
 * Callers supply labels; never invents an em-dash for a hidden empty layer.
 */
export function shareCopyChronologyLines(
  vm: ShareViewModel,
  actions: StreetAction[],
  opts: {
    streetHeader: (street: Street) => string
    actionVerb: (type: StreetAction['type']) => string
    bullet?: string
    /** Soft E P4: hero seat for display labels. */
    heroPosition?: string
  },
): string[] {
  if (!vm.layers.line) return []
  return buildHumanActionChronologyLines(actions, {
    streetHeader: opts.streetHeader,
    actionVerb: opts.actionVerb,
    bullet: opts.bullet,
    heroPosition: opts.heroPosition,
    // Unreachable when layers.line (actions non-empty); keep Soft-refuse clean.
    emptyLine: '',
  })
}

/** PNG chronology lines: omit entirely when no line layer (no em-dash block). */
export function sharePngChronologyLines(
  vm: ShareViewModel,
  actions: StreetAction[],
  heroPosition?: string,
): string[] {
  if (!vm.layers.line) return []
  return buildPngActionChronologyLines(actions, heroPosition)
}
